import React, { useState, useEffect, CSSProperties } from "react";

import IAccordionSection from "./IAccordionSection";
import { renderLabel, classNames } from "@features/utils/reactFunctions";
import useEventListener from "@profile/pages/AccountPage/features/useEventListener";

import * as style from "./AccordionSectionStyle.module.scss"

const mobileWidth = 991;
const pixelsPerSecond = 200;

export const AccordionSection = (props: IAccordionSection): JSX.Element => {
  const contentRef: React.RefObject<HTMLElement> = React.createRef();

  const [state, setState] = useState<{
    isMovable: boolean;
    isOpen: boolean;
    isMobile: boolean;
    height: number;
    transitionDuration: string;
    prevWidth: number;
  }>({
    isMovable: !!props.isMovable,
    isOpen: !!props.isOpen,
    isMobile: false,
    height: 0,
    transitionDuration: "1s",
    prevWidth: 0,
  });

  useEffect(() => {
    onResize();
  }, []);

  const calculateTransitionDuration = (): string =>
    `${Math.max(1, getContentHeight() / pixelsPerSecond)}s`;

  const onResize = (): void => {
    const width = window.innerWidth;

    if (width !== state.prevWidth) {
      const result: boolean = width < (props.mobileWidth || mobileWidth);
      const isNewDevice: boolean = result && !state.isMobile;

      setState({
        ...state,
        isMobile: result,
        isMovable: result ? true : !!props.isMovable,
        isOpen: isNewDevice ? false : state.isOpen,
        height: isNewDevice ? 0 : state.height,
        transitionDuration: calculateTransitionDuration(),
        prevWidth: width,
      });

      if (contentRef.current && state.isOpen) {
        contentRef.current.style.height = `${getContentHeight()}px`;
      }
    }
  };

  useEventListener("resize", onResize);

  const getContentHeight = (): number => {
    if (!contentRef.current) {
      return 0;
    }

    let result = 0;
    for (let i = 0; i < contentRef.current!.children.length; i += 1) {
      const child = contentRef.current!.children[i];
      const style = window.getComputedStyle(child);

      result += child.clientHeight;
      result += parseInt(style.marginTop || "0", 10);
      result += parseInt(style.marginBottom || "0", 10);
    }

    return result;
  };

  const handleTabClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();

    if (props.onClick) {
      props.onClick();
    }

    setState({
      ...state,
      height: !state.isOpen ? getContentHeight() : 0,
      isOpen: !state.isOpen,
    });
  };

  const renderButton = (): JSX.Element | boolean | undefined =>
    (state.isMovable || state.isMobile) && (
      <button
        className={classNames(
          style.accordionSectionButton,
          state.isOpen && style.accordionSectionButtonOpen
        )}
      />
    );

  const getStyles = (): CSSProperties =>
    state.isMovable
      ? {
          height: `${state.height}px`,
          overflow: "hidden",
          transitionDuration: state.transitionDuration,
        }
      : {};

  return (
    <div className={classNames(style.accordionSectionWrapper, props.className)}>
      <header className={style.accordionSectionHeader} onClick={handleTabClick}>
        {renderButton()}
        {renderLabel(
          props.label,
          `${style.accordionSectionLabel} ${
            !props.clickableLabel ? style.accordionSectionLabelNonClickable : ""
          }`
        )}
      </header>
      <main
        ref={contentRef}
        style={getStyles()}
        className={style.accordionSectionContent}
      >
        {props.children}
      </main>
    </div>
  );
};

export default React.memo(AccordionSection);
